import { apiRoutes } from "api/data/routes";
import { useBaseQuery } from "hooks/query.hook";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Typography, Button, Space, Avatar } from "antd";
import {
  ArrowLeftOutlined,
  ShareAltOutlined,
  CreditCardOutlined,
  MobileOutlined,
  BankOutlined,
  NumberOutlined,
  CalendarOutlined,
  BranchesOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import { copyToclipBoard } from "utils";
import { routes } from "data/routes";
import { TransactionField } from "components/transactionField";
import moment from "moment";
const { Text } = Typography;

const WithdrawalDetails = () => {
  const { t } = useTranslation("balance");
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: withdrawalData,
    error: withdrawalError,
    isLoading: loading,
    get: getWithdrawalData,
  } = useBaseQuery(`${apiRoutes.ACCOUNT_WITHDRAWAL}/${id}`);

  useEffect(() => {
    getWithdrawalData();
  }, []);

  const formatAmount = (amount: string) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XAF",
    }).format(parseFloat(amount));
  };

  if (loading) {
    return (
      <div className="transaction-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <Text className="loading-text">{t("loading", { ns: "common" })}</Text>
        </div>
      </div>
    );
  }

  if (!withdrawalData) {
    return (
      <div className="transaction-detail-page">
        <div className="error-container">
          <h1 className="error-title">{t("withdraw.transactionNotFound")}</h1>
          <p className="error-message">{t("withdraw.transactionNotFoundMessage")}</p>
          <button className="error-button" onClick={() => navigate(routes.BALANCE)}>
            <ArrowLeftOutlined />
            {t("back", { ns: "common" })}
          </button>
        </div>
      </div>
    );
  }

  const elements = [
    {
      icon: <NumberOutlined />,
      label: t("table.id"),
      value: withdrawalData?.data?.reference,
    },
    {
      icon: <CreditCardOutlined />,
      label: t("table.amount"),
      value: formatAmount(withdrawalData?.data.amount),
      copyable: true,
    },
    {
      icon: <BankOutlined />,
      label: t("methodTypes.mobile.fields.operator"),
      value: withdrawalData?.data?.provider,
    },
    {
      icon: <DeploymentUnitOutlined />,
      label: t("methodTypes.crypto.fields.crypto"),
      value: withdrawalData?.data?.ejaraCode,
    },
    {
      icon: <MobileOutlined />,
      label: t("methodTypes.mobile.fields.phone"),
      value: withdrawalData?.data?.phoneNumber,
      copyable: true,
    },
    {
      icon: <BranchesOutlined />,
      label: t("methodTypes.crypto.fields.address"),
      value: withdrawalData?.data?.cryptoAddress,
      copyable: true,
    },
    {
      icon: <NumberOutlined />,
      label: t("table.ref"),
      value: withdrawalData?.data?.paymentReference,
      copyable: true,
    },
    {
      icon: <CalendarOutlined />,
      label: t("table.date"),
      value: `${moment(withdrawalData?.data?.createdAt).format(
        "DD/MM/YYYY"
      )} ${t("at", { ns: "common" })} ${moment(
        withdrawalData?.data?.createdAt
      ).format("HH:mm")}`,
    },
  ];

  return (
    <div className="transaction-detail-containeer">
      <div className="transaction-detail-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(routes.BALANCE)}
          className="text-gray"
        >
          {t("back", { ns: "common" })}
        </Button>

        <Space>
          <Button
            icon={<ShareAltOutlined />}
            onClick={() => copyToclipBoard(window.location.href)}
          >
            {t("share", { ns: "common" })}
          </Button>
        </Space>
      </div>

      <Card className="transaction-card">
        <div className="transaction-card-header">
          <div className="header-content">
            <div className="avatar-parent">
              <Avatar
                size={80}
                icon={<CreditCardOutlined />}
                style={{
                  backgroundColor: "#6366f1",
                  flexShrink: 0,
                }}
              />
              <div
                className={`${withdrawalData?.data?.status.toLocaleLowerCase()} status-indicator`}
              >
                <div className="status-dot" />
              </div>
            </div>
            <div className="header-info">
              <h1 className="transaction-title">
                {withdrawalData?.data?.reference}
              </h1>
              <div className="transaction-meta">
                <Text className="method-text">
                  {withdrawalData?.data?.method}
                </Text>
                <span
                  className={`status ${withdrawalData?.data?.status.toLocaleLowerCase()}`}
                >
                  {t(
                    `status.${withdrawalData?.data?.status.toLowerCase()}`
                  ).toLocaleLowerCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="transaction-details-section">
          {elements
            .filter((elt) => !!elt.value)
            .map((elt) => (
              <TransactionField
                icon={elt.icon}
                label={elt.label}
                value={elt.value}
                copyable={elt.copyable}
              />
            ))}
        </div>
      </Card>
    </div>
  );
};

export default WithdrawalDetails;
